export interface IPoint {
    x: number;
    y: number;
}

export interface ITimePoint extends IPoint {
    t: number;
}

export interface ISize {
    width: number;
    height: number;
}

export interface IRectangle extends IPoint, ISize {
}

export interface IBounds {
    top: number;
    left: number;
    right: number;
    bottom: number;
}

export const PointUtil = {
    distance(p1: IPoint, p2: IPoint): number {
        const x = isNaN(p1.x) || isNaN(p2.x) ? 0 : p1.x - p2.x;
        const y = isNaN(p1.y) || isNaN(p2.y) ? 0 : p1.y - p2.y;
        return Math.sqrt(x * x + y * y);
    },
    length(p: IPoint | number, y?: number): number {
        if (typeof p === "number") {
            return Math.sqrt(p * p + y! * y!);
        }
        return Math.sqrt(p.x * p.x + p.y * p.y);
    },
    normalize(p: IPoint, length = 1): IPoint {
        const l = Math.sqrt(p.x * p.x + p.y * p.y);
        p.x *= length / l;
        p.y *= length / l;
        return p;
    },
    multiply(p: IPoint, value: number): IPoint {
        p.x *= value;
        p.y *= value;
        return p;
    },
    orthogonal(p: IPoint, sign = 1): IPoint {
        if (sign < 0) {
            const t = p.x;
            p.x = p.y;
            p.y = -t;
        } else {
            const t = p.x;
            p.x = -p.y;
            p.y = t;
        }
        return p;
    },
    opposite(p: IPoint): IPoint {
        p.x = -p.x;
        p.y = -p.y;
        return p;
    },
};

export const RectangleUtil = {
    unionRect(r1: IRectangle, r2: IRectangle, result?: IRectangle): IRectangle {
        const x1 = Math.min(r1.x, r2.x);
        const x2 = Math.max(r1.x + r1.width, r2.x + r2.width);
        const y1 = Math.min(r1.y, r2.y);
        const y2 = Math.max(r1.y + r1.height, r2.y + r2.height);
        result = result || {x: 0, y: 0, width: 0, height: 0};
        result.x = x1;
        result.width = x2 - x1;
        result.y = y1;
        result.height = y2 - y1;
        return result;
    },
    intersectionRect(r1: IRectangle, r2: IRectangle, result?: IRectangle): IRectangle | undefined {
        const x1 = Math.max(r1.x, r2.x);
        const x2 = Math.min(r1.x + r1.width, r2.x + r2.width);
        if (x1 < x2) {
            const y1 = Math.max(r1.y, r2.y);
            const y2 = Math.min(r1.y + r1.height, r2.y + r2.height);
            if (y1 < y2) {
                result = result || {x: 0, y: 0, width: 0, height: 0};
                result.x = x1;
                result.width = x2 - x1;
                result.y = y1;
                result.height = y2 - y1;
                return result;
            }
        }
        return undefined;
    },
    intersectionArea(r1: IRectangle, r2: IRectangle): number {
        const x1 = Math.max(r1.x, r2.x);
        const x2 = Math.min(r1.x + r1.width, r2.x + r2.width);
        if (x1 < x2) {
            const y1 = Math.max(r1.y, r2.y);
            const y2 = Math.min(r1.y + r1.height, r2.y + r2.height);
            if (y1 < y2) {
                return (x2 - x1) * (y2 - y1);
            }
        }
        return 0;
    },
    isIntersection(r1: IRectangle, r2: IRectangle): boolean {
        const x1 = Math.max(r1.x, r2.x);
        const x2 = Math.min(r1.x + r1.width, r2.x + r2.width);
        if (x1 < x2) {
            const y1 = Math.max(r1.y, r2.y);
            const y2 = Math.min(r1.y + r1.height, r2.y + r2.height);
            if (y1 < y2) {
                return true;
            }
        }
        return false;
    },
    contains(r: IRectangle, x: IPoint | number, y?: number) {
        if (typeof x !== "number") {
            y = x.y;
            x = x.x;
        }
        y = y || 0;
        return x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height;
    },
    /**
     *
     * @param base
     * @param r
     * @return bitmask of 9 squares where r is located related to base:
     *  1 - top
     *  2 - middle
     *  4 - bottom
     *  8 - left
     *  16 - center
     *  32 - right
     */
    getLocation(base: IRectangle, r: IRectangle): number {
        let n = 0;
        if (r.y < base.y) {
            n |= 1;
            if (r.y + r.height > base.y) {
                n |= 2;
            }
            if (r.y + r.height > base.y + base.height) {
                n |= 4;
            }
        } else if (r.y < base.y + base.height) {
            n |= 2;
            if (r.y + r.height > base.y + base.height) {
                n |= 4;
            }
        } else {
            n |= 4;
        }
        if (r.x < base.x) {
            n |= 8;
            if (r.x + r.width > base.x) {
                n |= 16;
            }
            if (r.x + r.width > base.x + base.width) {
                n |= 32;
            }
        } else if (r.x < base.x + base.width) {
            n |= 16;
            if (r.x + r.width > base.x + base.width) {
                n |= 32;
            }
        } else {
            n |= 32;
        }
        return n;
    }
};

export type Color = string | number;
export const ColorUtil = {
    toNumber(value: Color): number {
        if (typeof value === "undefined") {
            return NaN;
        }
        if (typeof value === "number") {
            return value;
        }
        return parseInt("0x" + value.replace("#", ""), 10);
    },
    toString(value: Color): string {
        if (typeof value === "undefined") {
            return "";
        }
        if (typeof value === "string") {
            return value;
        }
        return "#" + value.toString(16).replace("0x", "");
    },
    red(value: Color): number {
        return (ColorUtil.toNumber(value) >> 16) & 0xff;
    },
    green(value: Color): number {
        return (ColorUtil.toNumber(value) >> 8) & 0xff;
    },
    blue(value: Color): number {
        return ColorUtil.toNumber(value) & 0xff;
    },
    fromRGB(r: number, g: number, b: number): number {
        return ((r << 16) & 0xff0000) +
            ((g << 8) & 0x00ff00) +
            (b & 0x0000ff);
    },
    interpolate(fromColor: Color, toColor: Color, value: number): number {
        const n0 = ColorUtil.toNumber(fromColor);
        const n1 = ColorUtil.toNumber(toColor);
        return ColorUtil.fromRGB(
            ColorUtil.red(n0) + (ColorUtil.red(n1) - ColorUtil.red(n0)) * value,
            ColorUtil.green(n0) + (ColorUtil.green(n1) - ColorUtil.green(n0)) * value,
            ColorUtil.blue(n0) + (ColorUtil.blue(n1) - ColorUtil.blue(n0)) * value,
        );
    },
};

export const PolygonUtil = {
    isClockwise(poly: IPoint[]) {
        let sum = 0;
        for (let i = 0; i < poly.length; i++) {
            const p = poly[i];
            const q = poly[i + 1] || poly[0];
            sum += (q.x - p.x) * (q.y + p.y);
        }
        return sum < 0;
    }
}